import { useEffect, useRef } from 'react';
import type { ChannelType } from '@/models';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { getDeviceMetadata } from '../device-metadata';
import { useStatus } from '@/context/status-context/use-status.ts';
import { uuidv4 } from '@/lib/uuid';

export const deviceID = uuidv4();

export interface PresencePingerProps {
  userId: string;
  serverId?: string;
  channelId?: string;
  memberId?: string;
  conversationId?: string;
  serverName?: string;
  channelName?: string;
  channelType?: ChannelType;
  avatar?: string;
  name?: string;
  status?: string;
  description?: string;
  speaking?: boolean;
  mic?: boolean;
  raisedHand?: boolean;
}

export default function PresencePinger({
  userId,
  serverId,
  channelId,
  conversationId,
  serverName,
  channelName,
  channelType,
  avatar,
  name,
  speaking,
  mic,
  raisedHand,
}: PresencePingerProps) {
  const socket = useSocket();
  const { status, description } = useStatus();
  const deviceData = getDeviceMetadata(navigator.userAgent);
  const joinedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (channelId && !joinedAtRef.current) {
      joinedAtRef.current = Date.now();
    }

    if (!channelId) {
      joinedAtRef.current = null;
    }
  }, [channelId]);

  useEffect(() => {
    if (!socket) return;

    const pingPresence = () => {
      socket.send(
        JSON.stringify({
          type: 'update-presence',
          userId,
          deviceId: deviceID,
          status,
          description,
          avatar,
          name,
          deviceInfo: {
            osName: deviceData.osName,
            osVersion: deviceData.osVersion,
            browserName: deviceData.browserName,
            browserVersion: deviceData.browserVersion,
            arch: deviceData.arch,
            serverId,
            channelId,
            conversationId,
            serverName,
            channelName,
            channelType,
            speaking,
            mic,
            raisedHand,
            joinedAt: joinedAtRef.current,
          },
        })
      );
    };

    pingPresence();

    const intervalId = setInterval(pingPresence, 1 * 1000);
    return () => clearInterval(intervalId);
  }, [
    socket,
    userId,
    serverId,
    channelId,
    conversationId,
    serverName,
    channelName,
    channelType,
    status,
    avatar,
    name,
    speaking,
    mic,
    raisedHand,
    description,
  ]);

  return null;
}
